import React from 'react';

import { observer } from 'mobx-react';
import { reverse } from 'named-urls';

import { Add } from '@mui/icons-material';

import { Button, DangerButton, SecondaryButton } from 'components/Button';
import CustomModal from 'components/CustomModal';
import { BackgroundInformationSections } from 'components/Entities/EntityDetailsTab/styles';
import type { TabComponentProps } from 'components/Entities/EntityDetailsContent/types';
import { defaultProps as relatedGridProps } from 'components/Entities/EntityRelatedTab/components/types';
import { DataGridContainer } from 'components/Entities/EntityRelatedTab/components/styles';
import Grid from 'components/Grid';

import { CONTROL_FIELD } from 'containers/Controls/constants';
import { ControlRow } from 'containers/Controls/types';

import routes from 'core/routes';

import { useStore } from 'utils/hooks/store';
import { canLinkRiskControls } from 'utils/roles/permissions';

import AddRiskControlModal from '../AddRiskControlModal';

import { nameOf, riskControlColumns } from './columns';
import { TabToolbar } from './styles';

const RiskControlsTab: React.FunctionComponent<TabComponentProps> = ({ store }) => {
  const { notification, session, routing, riskDetails } = useStore();
  const [adding, setAdding] = React.useState(false);
  const [removing, setRemoving] = React.useState<ControlRow | null>(null);
  const refreshGrid = React.useRef<() => void>(() => undefined);

  const riskUuid = store.details?.uuid;
  if (!riskUuid) {
    return <></>;
  }

  const canLink = canLinkRiskControls(session.user);
  const openControl = (row: ControlRow): void =>
    routing.push(reverse(routes.dashboard.risks.controlDetails, { entityId: row.uuid }));

  const added = (): void => {
    setAdding(false);
    notification.enqueueSuccessSnackbar('Control added to this risk');
    refreshGrid.current();
  };

  const remove = async (row: ControlRow): Promise<void> => {
    setRemoving(null);
    if (await riskDetails.removeControl(riskUuid, row.uuid)) {
      refreshGrid.current();
    }
  };

  return (
    <BackgroundInformationSections data-testid="risk-controls-tab">
      {canLink && (
        <TabToolbar>
          <Button
            variant="text"
            size="medium"
            startIcon={<Add />}
            onClick={() => setAdding(true)}
            data-testid="risk-controls-add-button"
          >
            Add control
          </Button>
        </TabToolbar>
      )}
      <DataGridContainer>
        <Grid
          {...relatedGridProps}
          basicColumns={riskControlColumns({ onOpen: openControl, onRemove: canLink ? setRemoving : undefined })}
          entity="controls"
          isEntityList
          dataURL={`/api/entities/risks/${riskUuid}/linked-controls/`}
          fieldsURL="/api/entities/controls/fields/"
          customNoRowsText="No controls on this risk yet."
          sortBy={{ field: CONTROL_FIELD.name, sort: 'asc' }}
          gridPagination
          pageSize={20}
          disableSavePreferences
          onRowClick={({ row }) => openControl(row as ControlRow)}
          onRefreshCallback={(callback) => {
            refreshGrid.current = callback;
          }}
          testId="risk-controls-grid"
        />
      </DataGridContainer>
      <CustomModal
        modalWidth="sm"
        openModal={Boolean(removing)}
        onClose={() => setRemoving(null)}
        modalTitle="Remove control from this risk?"
        modalDescription={`${removing ? nameOf(removing) : ''} comes off this risk. It stays in the controls library, with its status and owner, on any other risks it's on.`}
        cancelButton={
          <SecondaryButton size="medium" onClick={() => setRemoving(null)} autoFocus>
            Cancel
          </SecondaryButton>
        }
        submitButton={
          <DangerButton
            size="medium"
            onClick={() => removing && remove(removing)}
            data-testid="risk-control-remove-confirm"
          >
            Remove
          </DangerButton>
        }
      />
      {adding && <AddRiskControlModal riskUuid={riskUuid} onClose={() => setAdding(false)} onAdded={added} />}
    </BackgroundInformationSections>
  );
};

export default observer(RiskControlsTab);
